import { useId, type ReactNode } from "react";
import { Inbox, Send, TriangleAlert } from "lucide-react";
import { RadioGroup, RadioGroupItem } from "@/components/shadcn/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/shadcn/select";
import { describeDueDays, directionText } from "../emailOptionsText";
import type { EmailDirection, EmailOptionsActivity, EmailTaskRule } from "../types";
import { WorkflowStep } from "./WorkflowStep";

// Radix Select doesn't allow an empty value, so "no task" needs a sentinel.
const NO_TASK = "none";

type EmailTaskRuleStepProps = {
  direction: EmailDirection;
  rule: EmailTaskRule;
  activities: EmailOptionsActivity[];
  dueDayOptions: number[];
  /** False while auto-create is off, so the rule can't run. */
  enabled: boolean;
  onChange: (changes: Partial<EmailTaskRule>) => void;
};

/** The rule for one direction, as its own short sequence: which task, then its status, then when it's due. */
export const EmailTaskRuleStep = ({
  direction,
  rule,
  activities,
  dueDayOptions,
  enabled,
  onChange,
}: EmailTaskRuleStepProps) => {
  const id = useId();
  const createsTask = !!rule.activityId;
  const isOpenTask = createsTask && !rule.completeTask;
  const text = directionText[direction];

  return (
    <WorkflowStep
      icon={direction === "inbound" ? Inbox : Send}
      title={text.title}
      description={text.description}
      inactive={!enabled || !createsTask}
    >
      <ol className="grid gap-4">
        <SubStep label="Create" htmlFor={`${id}-activity`}>
          <Select
            value={rule.activityId ?? NO_TASK}
            onValueChange={value => onChange({ activityId: value === NO_TASK ? null : value })}
            disabled={!enabled}
          >
            <SelectTrigger id={`${id}-activity`} className="w-full sm:w-80">
              <SelectValue />
            </SelectTrigger>
            {/* The shared SelectContent's max height uses Tailwind v3 syntax and doesn't apply
                under v4, so long activity lists are capped here to keep them scrollable. */}
            <SelectContent className="max-h-72">
              <SelectItem value={NO_TASK}>Don't create a task</SelectItem>
              {activities.map(activity => (
                <SelectItem key={activity.id} value={activity.id}>
                  {activity.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {activities.length === 0 && (
            <p className="mt-2 text-sm text-muted-foreground">
              There are no activities to choose from. Add one in Activity Maintenance first.
            </p>
          )}
        </SubStep>

        {createsTask && (
          <SubStep label="Then">
            <RadioGroup
              value={rule.completeTask ? "complete" : "open"}
              onValueChange={value => onChange({ completeTask: value === "complete" })}
              disabled={!enabled}
              className="grid gap-3 sm:grid-cols-2"
              aria-label={`What happens to the task for ${text.title.toLowerCase()}`}
            >
              <StatusChoice
                id={`${id}-complete`}
                value="complete"
                title="Log it as completed"
                description="Records the email on the customer's history. Nothing to follow up."
              />
              <StatusChoice
                id={`${id}-open`}
                value="open"
                title="Leave it open to follow up"
                description="Stays on task lists, and shows on the email, until someone completes it."
              />
            </RadioGroup>
          </SubStep>
        )}

        {isOpenTask && (
          <SubStep label="Due" htmlFor={`${id}-due`}>
            <Select
              value={String(rule.dueDays)}
              onValueChange={value => onChange({ dueDays: Number(value) })}
              disabled={!enabled}
            >
              <SelectTrigger id={`${id}-due`} className="w-full sm:w-80">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {dueDayOptions.map(days => (
                  <SelectItem key={days} value={String(days)}>
                    {describeDueDays(days)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {rule.dueDays === 0 && (
              <p className="mt-2 flex items-start gap-1.5 text-sm text-muted-foreground">
                <TriangleAlert className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden />
                The task is due when the email arrives, so it shows as overdue straight away.
              </p>
            )}
          </SubStep>
        )}
      </ol>
    </WorkflowStep>
  );
};

type SubStepProps = {
  label: string;
  htmlFor?: string;
  children: ReactNode;
};

const SubStep = ({ label, htmlFor, children }: SubStepProps) => (
  <li className="grid gap-2 sm:grid-cols-[4rem_1fr] sm:items-start">
    <label htmlFor={htmlFor} className="pt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {label}
    </label>
    <div className="min-w-0">{children}</div>
  </li>
);

type StatusChoiceProps = {
  id: string;
  value: string;
  title: string;
  description: string;
};

const StatusChoice = ({ id, value, title, description }: StatusChoiceProps) => (
  <label
    htmlFor={id}
    className="flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors hover:bg-accent/50 has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5 has-[:disabled]:cursor-not-allowed"
  >
    <RadioGroupItem id={id} value={value} className="mt-0.5" />
    <span className="grid gap-0.5">
      <span className="text-sm font-medium">{title}</span>
      <span className="text-sm text-muted-foreground">{description}</span>
    </span>
  </label>
);
